/**
 * Nel FORK alcuni menu del modello non servono piu', perche' il pannello mappa fa il loro lavoro:
 *  - "Scelta indirizzo" (i pulsanti 1-5 "Quale indirizzo?"): il suggerimento si sceglie dalla tendina;
 *  - "Posizione del punto" (Al centro / In alto / ...): il segnaposto si sposta trascinando la mappa.
 * Qui li NASCONDIAMO; tutti gli altri menu a pulsanti (Zoom mappa, Modello, Misura, ...) restano
 * il componente standard di ShapeDiver. Il modello ShapeDiver non cambia: nel configuratore standard
 * quei menu continuano a esistere.
 *
 * SEGNAPOSTO (26 set 2026, richiesta di Davide): i pulsanti mostrano il SIMBOLO invece della scritta
 * (punto luce = diamantino, casetta, cerchio, cuore; "nessuno" = cerchio barrato). La voce "— scegli —" non ha pulsante:
 * finche' il cliente non tocca un simbolo, nessuno e' evidenziato. Il nome resta nel tooltip.
 */
import {useParameterComponentCommons} from "@AppBuilderLib/entities/parameter/model/useParameterComponentCommons";
import {
	PropsParameterComponent,
	PropsParameterWrapper,
} from "@AppBuilderLib/entities/parameter/config/propsParameter";
import ParameterLabelComponent from "@AppBuilderLib/entities/parameter/ui/ParameterLabelComponent";
import ParameterSelectComponent from "@AppBuilderLib/entities/parameter/ui/ParameterSelectComponent";
import ParameterWrapperComponent from "@AppBuilderLib/entities/parameter/ui/ParameterWrapperComponent";
import TooltipWrapper from "@AppBuilderLib/shared/ui/tooltip/TooltipWrapper";
import {Button, Flex} from "@mantine/core";

// nome del parametro nel modello, oppure nome mostrato nell'App Builder
// "Zoom mappa" (28 set 2026): nascosto, lo zoom si fa con le dita sulla mappa (vedi EidosMappaComponent, "zoomend")
const NASCOSTI = ["Scelta indirizzo", "Quale indirizzo?", "Posizione del punto", "Zoom mappa"];
const SEGNAPOSTO = "Segnaposto";

/** simboli (SVG 24x24, linea 1.8) per ogni voce della lista Segnaposto, riconosciuti dal nome */
function Simbolo({nome}: {nome: string}) {
	const n = nome.toLowerCase();
	const comune = {
		width: 26,
		height: 26,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 1.8,
		strokeLinejoin: "round" as const,
		strokeLinecap: "round" as const,
	};
	// "Punto luce" (23 ott 2026) = nuovo nome del diamantino (zircone)
	if (n.includes("diamant") || n.includes("punto luce"))
		return (
			<svg {...comune}>
				<path d="M7 4h10l4 5-9 12L3 9z" />
				<path d="M3 9h18M9 4l3 5 3-5M7 9l5 12M17 9l-5 12" />
			</svg>
		);
	if (n.includes("caset"))
		return (
			<svg {...comune}>
				<path d="M3 11 12 3l9 8" />
				<path d="M5.5 9.5V20h13V9.5" />
				<path d="M10 20v-6h4v6" />
			</svg>
		);
	if (n.includes("cerch"))
		return (
			<svg {...comune}>
				<circle cx="12" cy="12" r="7.5" />
			</svg>
		);
	if (n.includes("cuor"))
		return (
			<svg {...comune}>
				<path d="M12 20.5 4.3 12.9A4.6 4.6 0 0 1 10.8 6.4L12 7.6l1.2-1.2a4.6 4.6 0 0 1 6.5 6.5z" />
			</svg>
		);
	if (n.includes("nessun"))
		return (
			<svg {...comune}>
				<circle cx="12" cy="12" r="7.5" />
				<path d="M6.7 6.7l10.6 10.6" />
			</svg>
		);
	return <span>{nome}</span>;
}

/**
 * (24 ott 2026, Davide: «la parte segnaposto e' troppo risicata, troppo semplice») SCHEDA DEL SEGNAPOSTO SCELTO, sotto
 * i simboli: foto vera del segnaposto sul gioiello (ritaglio dei render macro della collana Hestia, su R2), nome,
 * sovrapprezzo e una frase. I prezzi sono quelli del riquadro VAR 3 del modello (Punto luce +10, smalto +10): se
 * cambiano li', vanno cambiati anche qui. Il testo del Punto luce e' quello scelto da Davide il 23 ott ("zircone",
 * mai "diamante").
 */
const FOTO_SEGNAPOSTO = "https://eidos-strade.eidos-direzionale.workers.dev/foto/render_segnaposto_";
type InfoSegnaposto = {foto: string; prezzo?: string; testo: string};
function infoSegnaposto(nome: string): InfoSegnaposto | null {
	const n = nome.toLowerCase();
	if (n.includes("nessun")) return {foto: "nessuno", testo: "Nessun simbolo: sul gioiello resta solo la mappa del tuo luogo."};
	if (n.includes("punto luce") || n.includes("diamant"))
		return {
			foto: "diamantino",
			prezzo: "+10 €",
			testo: "Uno zircone bianco incastonato a mano sul segnaposto, per far brillare il luogo che hai scelto.",
		};
	if (n.includes("caset")) return {foto: "casetta", testo: "Una piccola casa segna il luogo che hai scelto. Puoi colorarla con lo smalto (+10 €)."};
	if (n.includes("cerch")) return {foto: "cerchio", testo: "Un cerchio segna il luogo che hai scelto. Puoi colorarlo con lo smalto (+10 €)."};
	if (n.includes("cuor")) return {foto: "cuore", testo: "Un cuore segna il luogo che hai scelto. Puoi colorarlo con lo smalto (+10 €)."};
	return null;
}

function SegnapostoComponent(props: PropsParameterComponent & Partial<PropsParameterWrapper>) {
	const {definition, value, handleChange, onCancel, disabled} = useParameterComponentCommons<string>(props, 0);
	const voci = (definition?.choices ?? []) as string[];
	const {wrapperComponent, wrapperProps} = props;
	const nomeScelto = voci[parseInt(String(value ?? ""), 10)] ?? "";
	const info = /scegli/i.test(nomeScelto) ? null : infoSegnaposto(nomeScelto);
	return (
		<ParameterWrapperComponent onCancel={onCancel} component={wrapperComponent ?? "section"} {...(wrapperProps ?? {})}>
			<ParameterLabelComponent {...props} cancel={onCancel} />
			<Flex gap="xs" wrap="wrap" className="eidos-segnaposto">
				{voci.map((nome, i) => {
					// la voce "— scegli —" non ha un pulsante
					if (/scegli/i.test(nome) || /^[—–-]/.test(nome.trim())) return null;
					const scelto = String(value ?? "") === String(i);
					return (
						<TooltipWrapper key={nome} label={nome}>
							<Button
								variant={scelto ? "filled" : "default"}
								disabled={disabled}
								aria-label={nome}
								onClick={() => handleChange(String(i))}
								px={12}
								styles={{label: {display: "flex", alignItems: "center"}}}
							>
								<Simbolo nome={nome} />
								{/* (24 ott 2026) sul telefono il tooltip non c'e': il nome compare sotto il simbolo (solo CSS del telefono) */}
								<span className="eidos-segnaposto-nome">{nome}</span>
							</Button>
						</TooltipWrapper>
					);
				})}
			</Flex>
			{info ? (
				<div className="eidos-segnaposto-info">
					{/* (27 ott 2026) v=2: casetta nera, cerchio tiffany, cuore rosso (gli smalti veri; prima blu e verde) */}
				<img src={FOTO_SEGNAPOSTO + info.foto + ".jpg?v=2"} alt={nomeScelto} width={64} height={64} />
					<div>
						<div className="eidos-segnaposto-info-titolo">
							{nomeScelto}
							{info.prezzo ? <span className="eidos-segnaposto-info-prezzo">{info.prezzo}</span> : null}
						</div>
						<div className="eidos-segnaposto-info-testo">{info.testo}</div>
					</div>
				</div>
			) : (
				// (27 ott 2026, Davide: «quando ancora non clicchi, piu' evidente che deve cliccare su una scelta, come quando
				// metti una scelta») stessa scheda della scelta fatta, ma con bordo ottanio, segnaposto disegnato e un
				// richiamo che pulsa tre volte (fermo se il telefono chiede meno animazioni)
				<div className="eidos-segnaposto-info eidos-segnaposto-info-vuota" role="status">
					<div className="eidos-segnaposto-info-icona" aria-hidden="true">
						<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#134D5D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
							<path d="M12 21s-6-5.6-6-10.6a6 6 0 0 1 12 0C18 15.4 12 21 12 21z" />
							<circle cx="12" cy="10.4" r="2.2" />
						</svg>
					</div>
					<div>
						<div className="eidos-segnaposto-info-titolo">Scegli il segnaposto</div>
						<div className="eidos-segnaposto-info-testo">Tocca uno dei simboli qui sopra: segna il tuo luogo sul gioiello.</div>
					</div>
				</div>
			)}
		</ParameterWrapperComponent>
	);
}

/** colore di una voce dalle impostazioni del parametro ("r,g,b" di Grasshopper oppure #rrggbb) */
function coloreVoce(definition: unknown, nome: string): string | undefined {
	try {
		const s = (definition as {settings?: {itemData?: Record<string, {color?: string}>}}).settings;
		const c = s?.itemData?.[nome]?.color;
		if (!c) return undefined;
		if (/^\s*\d+\s*,\s*\d+\s*,\s*\d+\s*$/.test(c)) return `rgb(${c})`;
		return c;
	} catch {
		return undefined;
	}
}

/** (27 ott 2026, Davide: «gli smalti che abbiamo sono nero, rosso, tiffany e bianco») colori non piu' in vendita:
 *  spariscono dal menu anche nei modelli caricati prima della v47 */
const SMALTI_NON_IN_VENDITA = ["blu", "verde", "azzurro"];

/**
 * SMALTO (27 ott 2026, Davide: «la parte smalti non e' professionale come il resto, e il primo viene un po' tagliato»):
 * stessi pulsanti del segnaposto (Button Mantine, pieno ottanio quando e' scelto) con campione di colore + nome; prima
 * erano pallini rotondi con un anello esterno che usciva dal riquadro e veniva tagliato. Sul computer i pulsanti stanno
 * affiancati (campione e nome sulla stessa riga), sul telefono e sul totem 5 caselle uguali (campione sopra, nome sotto).
 */
function SmaltoComponent(props: PropsParameterComponent & Partial<PropsParameterWrapper>) {
	const {definition, value, handleChange, onCancel, disabled} = useParameterComponentCommons<string>(props, 0);
	const voci = (definition?.choices ?? []) as string[];
	const {wrapperComponent, wrapperProps} = props;
	const nomeScelto = voci[parseInt(String(value ?? ""), 10)] ?? "";
	return (
		<ParameterWrapperComponent onCancel={onCancel} component={wrapperComponent ?? "section"} {...(wrapperProps ?? {})}>
			<ParameterLabelComponent {...props} cancel={onCancel} />
			<div className="eidos-smalti">
				{voci.map((nome, i) => {
					if (SMALTI_NON_IN_VENDITA.includes(nome.trim().toLowerCase())) return null;
					const scelto = String(value ?? "") === String(i);
					const nessuno = /nessun/i.test(nome);
					const colore = coloreVoce(definition, nome) ?? "#e8e4de";
					return (
						<Button
							key={nome}
							variant={scelto ? "filled" : "default"}
							disabled={disabled}
							aria-label={nessuno ? "Senza smalto" : `Smalto ${nome}`}
							title={nessuno ? "Senza smalto" : nome}
							aria-pressed={scelto}
							onClick={() => handleChange(String(i))}
							className="eidos-smalti-voce"
						>
							{nessuno ? (
								<svg className="eidos-smalti-campione" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
									<circle cx="12" cy="12" r="9.5" />
									<path d="M5.3 5.3l13.4 13.4" />
								</svg>
							) : (
								<span className="eidos-smalti-campione" style={{background: colore}} />
							)}
							<span className="eidos-smalti-nome">{nome}</span>
						</Button>
					);
				})}
			</div>
			{/* (24 ott 2026) colore scelto e prezzo scritti sotto i pulsanti: sul telefono il tooltip non c'e' */}
			{nomeScelto ? (
				<div className="eidos-smalto-nota">
					{/nessun/i.test(nomeScelto) ? (
						"Senza smalto"
					) : (
						<>
							Smalto {nomeScelto.toLowerCase().replace("tiffany", "Tiffany")}
							<span className="eidos-smalto-nota-prezzo">+10 €</span>
						</>
					)}
				</div>
			) : null}
		</ParameterWrapperComponent>
	);
}

const SMALTO = "Smalto";

export default function EidosSceltaComponent(
	props: PropsParameterComponent & Partial<PropsParameterWrapper>,
) {
	const {definition} = useParameterComponentCommons<string>(props, 0);
	const nomi = [definition?.name, definition?.displayname].filter(Boolean) as string[];
	if (nomi.some((n) => NASCOSTI.includes(n))) return <></>;
	if (nomi.includes(SEGNAPOSTO)) return <SegnapostoComponent {...props} />;
	if (nomi.includes(SMALTO)) return <SmaltoComponent {...props} />;
	return <ParameterSelectComponent {...props} />;
}
